<template>
  <div>
    <Breadcrumb :breadcrumb="breadcrumb"></Breadcrumb>
    <h5 class="title is-5">{{ $t("devices.viewTitle") }}</h5>
    <div class="field has-addons">
      <p class="control">
        <router-link
          v-if="device.deviceType === 'ThermoCamera'"
          :to="{
            name: 'devicesLiveView',
            params: {
              projectId: projectId,
              deviceId: deviceId
            }
          }"
          class="button has-tooltip-arrow"
          :data-tooltip="$t('devices.live')"
        >
          <span class="icon is-small">
            <i class="fas fa-video"></i>
          </span>
          <span>{{ $t("Live") }}</span>
        </router-link>
      </p>
      <p class="control">
        <router-link
          :to="{
            name: 'devicesEdit',
            params: {
              projectId: projectId,
              deviceId: deviceId
            }
          }"
          class="button has-tooltip-arrow"
          :data-tooltip="$t('devices.edit')"
        >
          <span class="icon is-small">
            <i class="fas fa-edit"></i>
          </span>
          <span>{{ $t("Edit") }}</span>
        </router-link>
      </p>
      <p class="control">
        <router-link
          :to="{
            name: 'deviceImageUpload',
            params: {
              projectId: projectId,
              deviceId: deviceId
            }
          }"
          class="button has-tooltip-arrow"
          :data-tooltip="$t('devices.addPhoto')"
        >
          <span class="icon is-small">
            <i class="fas fa-image"></i>
          </span>
          <span>{{ $t("Photo") }}</span>
        </router-link>
      </p>
      <p class="control">
        <router-link
          v-if="device.deviceType === 'ThermoCamera'"
          :to="{
            name: 'deviceSurvey',
            params: {
              projectId: projectId,
              deviceId: deviceId
            }
          }"
          class="button has-tooltip-arrow"
          :data-tooltip="$t('devices.ansSurvey')"
        >
          <span class="icon is-small">
            <i class="fas fa-poll-h"></i>
          </span>
          <span>{{ $t("Survey") }}</span>
        </router-link>
      </p>
      <p class="control">
        <router-link
          v-if="device.deviceType === 'TrailCamera'"
          :to="{
            name: 'UndetectedArea',
            params: {
              projectId: projectId,
              deviceId: deviceId,
              undetectedArea: device.undetectedarea
            }
          }"
          class="button has-tooltip-arrow"
          :data-tooltip="$t('devices.undetectedarea')"
        >
          <span class="icon is-small">
            <i class="fas fa-poll-h"></i>
          </span>
          <span>{{ $t("Undetected") }}</span>
        </router-link>
      </p>
      <p class="control">
        <button
          @click="modalChange()"
          data-target="modal"
          class="button has-tooltip-arrow"
          :data-tooltip="$t('devices.delete')"
        >
          <span class="icon is-small">
            <i class="fas fa-trash-alt"></i>
          </span>
          <span>{{ $t("Delete") }}</span>
        </button>
      </p>
    </div>

    <div
      v-if="device.deviceSetting === 'JPEG' || device.heartBeatError"
      class="notification is-light is-danger"
    >
      {{ $t("devices.settingError") }}
      <ul>
        <li class="device-error" v-if="device.deviceSetting === 'JPEG'">
          {{ $t("devices.jpegError") }}
        </li>
        <li class="device-error" v-if="device.heartBeatError">
          {{ $t("devices.connectError") }}
        </li>
        <li class="device-error" v-if="device.heartBeatError">
          {{ $t("devices.connectError2") }}
        </li>
        <li class="device-error" v-if="device.heartBeatError">
          {{ $t("devices.connectError3") }}
          <ul>
            <li class="device-error-list">
              {{ $t("devices.connectError3-1") }}
            </li>
            <li class="device-error-list">
              {{ $t("devices.connectError3-2") }}
            </li>
          </ul>
        </li>
      </ul>
    </div>

    <div class="notification is-light is-info">
      {{ $t("devices.deviceInfo") }}
    </div>

    <ThermoCamera
      :device="device"
      v-if="device.deviceType === 'ThermoCamera'"
    />
    <div class="columns" v-if="device.deviceType === 'TrailCamera'">
      <div class="column">
        <TrailCamera :device="device" />
      </div>
      <div class="column">
        <AiAnalyze :chartData="aiAnalyze" v-if="aiAnalyze" />
      </div>
    </div>

    <div class="columns mt-4">
      <div class="column is-one-third" v-if="img01Date">
        <h5 class="title is-5 mb-0 image-title">
          {{ $t("devices.imageUpload.img01") }}
          <a
            class="ml-2"
            @click="showImageModal('img01')"
            :data-tooltip="$t('devices.deleteImage.image1')"
          >
            <span class="delete-icon">
              <i class="fas fa-trash-alt"></i>
            </span>
          </a>
          <a
            class="ml-2"
            @click="rotateImage('rotationAngle01')"
            :data-tooltip="$t('devices.rotateImage.image1')"
          >
            <span class="delete-icon">
              <i class="fas fa-redo-alt"></i>
            </span>
          </a>
          <a
            class="ml-2"
            @click="showEditImageModal('img01')"
            :data-tooltip="$t('devices.editImage.image1')"
          >
            <span class="delete-icon">
              <i class="fas fa-upload"></i>
            </span>
          </a>
        </h5>
        <p class="image-date">
          <DatetimeConverter :datetime="img01Date" />
        </p>
        <figure class="image mt-2">
          <img
            :src="image1"
            :style="{ transform: `rotate(${rotationAngle01}deg)` }"
            class="responsive-image"
          />
        </figure>
      </div>
      <div class="column is-one-third" v-if="img02Date">
        <h5 class="title is-5 mb-0 image-title">
          {{ $t("devices.imageUpload.img02") }}
          <a
            class="ml-2"
            @click="showImageModal('img02')"
            :data-tooltip="$t('devices.deleteImage.image2')"
          >
            <span class="delete-icon">
              <i class="fas fa-trash-alt"></i>
            </span>
          </a>
          <a
            class="ml-2"
            @click="rotateImage('rotationAngle02')"
            :data-tooltip="$t('devices.rotateImage.image2')"
          >
            <span class="delete-icon">
              <i class="fas fa-redo-alt"></i>
            </span>
          </a>
          <a
            class="ml-2"
            @click="showEditImageModal('img02')"
            :data-tooltip="$t('devices.editImage.image2')"
          >
            <span class="delete-icon">
              <i class="fas fa-upload"></i>
            </span>
          </a>
        </h5>
        <p class="image-date">
          <DatetimeConverter :datetime="img02Date" />
        </p>
        <figure class="image mt-2">
          <img
            :src="image2"
            :style="{ transform: `rotate(${rotationAngle02}deg)` }"
            class="responsive-image"
          />
        </figure>
      </div>
      <div
        class="column is-one-third"
        v-if="device.deviceType == 'TrailCamera' && img03Date"
      >
        <h5 class="title is-5 mb-0 image-title">
          {{ $t("devices.imageUpload.img03") }}
          <a
            class="ml-2"
            @click="showImageModal('img03')"
            :data-tooltip="$t('devices.deleteImage.image3')"
          >
            <span class="delete-icon">
              <i class="fas fa-trash-alt"></i>
            </span>
          </a>
        </h5>
        <p class="image-date">
          <DatetimeConverter :datetime="img03Date" />
        </p>
        <figure class="image mt-2">
          <img :src="image3" class="responsive-image" />
        </figure>
      </div>
    </div>

    <div class="modal" v-bind:class="{ 'is-active': modal }">
      <div class="modal-background"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t("devices.delete") }}</p>
        </header>
        <section class="modal-card-body">
          <div class="content">
            <table class="table">
              <thead></thead>
              <tbody>
                <tr>
                  <th width="30%">{{ $t("devices.deviceId") }}</th>
                  <td>{{ device.deviceId }}</td>
                </tr>
                <tr>
                  <th>{{ $t("devices.deviceLocate") }}</th>
                  <td>{{ device.deviceLocate }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="deleted">
              <AlertMessage
                color="is-danger"
                :message="$t('devices.deleteMessage')"
              ></AlertMessage>
            </div>
            <div v-else class="has-text-centered">
              <AlertMessage
                color="is-danger"
                :message="$t('devices.deletedMessage')"
              ></AlertMessage>
              <router-link
                :to="{
                  name: 'devicesList',
                  params: {
                    projectId: projectId
                  }
                }"
              >
                {{ $t("devices.backList") }}
              </router-link>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button @click="modalChange()" class="button">Cancel</button>
          <button
            @click="deleteDevice()"
            class="button is-danger"
            v-bind:class="{ 'is-loading': isLoad }"
          >
            {{ $t("Delete") }}
          </button>
        </footer>
      </div>
    </div>

    <Modal
      :imageModalView="imageModalView"
      :modalTitle="$t('devices.deleteImageTitle')"
      :modalAction="$t('Delete')"
      @close="closeImageModal"
      @doAction="deleteImage()"
    >
      <p>{{ modalMessage }}</p>
    </Modal>

    <Modal
      :imageModalView="imageEditModalView"
      :modalTitle="$t('devices.editImageTitle')"
      :modalAction="$t('Edit')"
      @close="closeEditImageModal"
      @doAction="editImage()"
    >
      <p>{{ editMessage }}</p>
    </Modal>
  </div>
</template>
<script>
import AlertMessage from "@/components/AlertMessage.vue";
import Breadcrumb from "@/components/Breadcrumb";
import Modal from "@/components/Modal";
import imsr from "@/imsr-api";
import imsrc from "@/imsr-common";
import { computed, ref } from "@vue/reactivity";
import { onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import AiAnalyze from "./aiAnalyze.vue";
import ThermoCamera from "./thermoCamera.vue";
import TrailCamera from "./trailCamera.vue";
import DatetimeConverter from "@/components/DatetimeConverter.vue";

export default {
  components: {
    AlertMessage,
    Breadcrumb,
    ThermoCamera,
    TrailCamera,
    Modal,
    AiAnalyze,
    DatetimeConverter
  },
  setup() {
    const analyze_limit = 7;
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const deviceId = route.params.deviceId;
    const device = ref({});
    const aiAnalyze = ref(null);
    const modal = ref(false);
    const deleted = ref(true);
    const imageModalView = ref(false);
    const modalMessage = ref("");
    const targetImage = ref("");
    const imageEditModalView = ref(false);
    const editMessage = ref("");
    const editImageType = ref("");
    const image1 = ref("/img/blank.png");
    const image2 = ref("/img/blank.png");
    const image3 = ref("/img/blank.png");
    const img01Date = ref("");
    const img02Date = ref("");
    const img03Date = ref("");
    const rotationAngle01 = ref(0); // 初期回転角度
    const rotationAngle02 = ref(0); // 初期回転角度
    const breadcrumb = ref([
      {
        title: "Home",
        path: {
          name: "dashboard",
          params: { projectId: route.params.projectId }
        }
      },
      {
        title: "Device Config",
        path: {
          name: "devicesList",
          params: { projectId: route.params.projectId }
        }
      },
      { title: route.params.deviceId, path: "#", isActive: true }
    ]);
    const isLoad = ref(false);
    const getDevice = async () => {
      try {
        const params = {
          tenantId: store.getters.tenantId,
          projectId: route.params.projectId,
          deviceId: route.params.deviceId
        };
        const res = await imsr.imsrGetApi("/devices", params);
        device.value = res;
        if (res.heartBeatUpdateTime) {
          device.value.heartBeatUpdateTime = imsrc.dateFormat(
            device.value.heartBeatUpdateTime
          );
        }
        device.value.created_at = imsrc.dateFormat(device.value.created_at);
        device.value.updated_at = imsrc.dateFormat(device.value.updated_at);
        if (device.value.undetectedarea) {
          device.value.undetectedarea = JSON.parse(
            JSON.stringify(device.value.undetectedarea)
          );
          const undetectedarea = JSON.parse(device.value.undetectedarea);
          if (undetectedarea.shape.length) {
            device.value.enabledUndetectedarea = true;
          } else {
            device.value.enabledUndetectedarea = false;
          }
        } else {
          device.value.enabledUndetectedarea = false;
        }
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };

    getDevice();

    const getImages = async () => {
      try {
        const paramsBase = {
          tenantId: store.getters.tenantId,
          projectId: route.params.projectId,
          deviceId: route.params.deviceId
        };

        // 共通化: 画像取得処理
        const fetchImage = async (
          imageType,
          imageRef,
          dateRef,
          dataKey = "rawData"
        ) => {
          const params = { ...paramsBase, imageType };
          const res = await imsr.imsrGetImageApi(params);
          if (res.file == "true") {
            imageRef.value = "data:image/jpeg;base64," + res[dataKey];
            dateRef.value = res.LastModified;
          }
        };

        await fetchImage("img01", image1, img01Date);
        await fetchImage("img02", image2, img02Date);

        if (device.value.deviceType === "TrailCamera") {
          // img03だけdataキーが異なる場合に対応
          await fetchImage("img03", image3, img03Date, "data");
        }
      } catch (e) {
        console.log("failed to get images"); // eslint-disable-line no-console
      }
    };

    getImages();

    const modalChange = () => {
      modal.value = !modal.value;
    };

    const deleteDevice = async () => {
      try {
        isLoad.value = true;
        const params = {
          tenantId: store.getters.tenantId,
          projectId: route.params.projectId,
          deviceId: route.params.deviceId
        };
        await imsr.imsrDeleteApi("/devices", params);
        isLoad.value = false;
        router.push({
          name: "devicesList",
          params: {
            projectId: route.params.projectId
          },
          replace: true
        });
        deleted.value = false;
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };

    const deleteImage = async () => {
      try {
        const params = {
          tenantId: store.getters.tenantId,
          projectId: route.params.projectId,
          deviceId: route.params.deviceId,
          imageType: targetImage.value
        };
        await imsr.imsrDeleteApi("/devices/image", params);
        closeImageModal();
        router.go({
          name: "devicesView",
          params: { deviceId: route.params.deviceId }
        });
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };

    const showImageModal = img => {
      if (img == "img01") {
        modalMessage.value = t("devices.deleteImage1");
      } else if (img == "img02") {
        modalMessage.value = t("devices.deleteImage2");
      } else {
        modalMessage.value = t("devices.deleteImage3");
      }
      targetImage.value = img;
      imageModalView.value = true;
    };

    const rotateImage = angleRef => {
      const angleMap = {
        rotationAngle01: rotationAngle01,
        rotationAngle02: rotationAngle02
      };

      if (angleMap[angleRef]) {
        angleMap[angleRef].value = (angleMap[angleRef].value + 90) % 360;
      }
    };

    const showEditImageModal = img => {
      if (img == "img01") {
        editMessage.value = t("devices.editImage1");
      } else {
        editMessage.value = t("devices.editImage2");
      }
      editImageType.value = img;
      imageEditModalView.value = true;
    };

    const editImage = async () => {
      const angleMap = {
        img01: rotationAngle01,
        img02: rotationAngle02
      };
      const imageMap = {
        img01: image1,
        img02: image2
      };

      try {
        isLoad.value = true;
        const ticketParams = {
          tenantId: store.getters.tenantId,
          projectId: route.params.projectId
        };
        const ticket = await imsr.imsrGetApi(
          "/projects/getTicket",
          ticketParams
        );
        const params = new FormData();
        params.append("tenantId", store.getters.tenantId);
        params.append("projectId", route.params.projectId);
        params.append("userId", store.getters.userId);
        params.append("deviceId", route.params.deviceId);
        params.append("imageType", editImageType.value);
        params.append("ticket", ticket.movieTicket);

        // Base64データをBlobに変換する関数
        const base64ToBlob = (base64, mime) => {
          const byteString = window.atob(base64.split(",")[1]);
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }
          return new Blob([ab], { type: mime });
        };

        // Base64データをBlobに変換
        const base64Data = imageMap[editImageType.value].value;
        const mimeType = base64Data.match(/data:(.*);base64,/)[1];
        const blob = base64ToBlob(base64Data, mimeType);

        // BlobをFileオブジェクトに変換
        const file = new File([blob], "image.jpeg", { type: mimeType });

        // FileオブジェクトをFormDataに追加
        params.append("file", file);
        params.append("orientation", angleMap[editImageType.value].value);
        await imsr.imsrImageUploadApi(params);
        isLoad.value = false;
        closeEditImageModal();
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };

    const closeEditImageModal = () => {
      imageEditModalView.value = false;
    };

    const closeImageModal = () => {
      imageModalView.value = false;
    };

    const retrieveAiAnalyze = async () => {
      try {
        const params = {
          tenantId: store.getters.tenantId,
          projectId: route.params.projectId,
          deviceId: deviceId,
          limit: analyze_limit
        };
        const res = await imsr.imsrGetApi("/devices/listAiAnalyze", params);
        aiAnalyze.value = res;
      } catch (e) {
        console.log("failed to retrieve ai analyze data"); // eslint-disable-line no-console
      }
    };
    onMounted(() => {
      retrieveAiAnalyze();
    });

    return {
      device,
      modal,
      image1,
      image2,
      image3,
      img01Date,
      img02Date,
      img03Date,
      modalChange,
      deleteDevice,
      deleteImage,
      deviceId,
      projectId: computed(() => route.params.projectId),
      breadcrumb,
      isLoad,
      deleted,
      imageModalView,
      showImageModal,
      closeImageModal,
      modalMessage,
      aiAnalyze,
      rotationAngle01,
      rotationAngle02,
      rotateImage,
      editImage,
      showEditImageModal,
      closeEditImageModal,
      editMessage,
      imageEditModalView
    };
  }
};
</script>
<style scoped>
.device-error {
  list-style: disc;
  margin-top: 0.5em;
  margin-left: 1em;
}
.delete-icon {
  font-size: 0.8em;
}
.device-error-list {
  list-style: decimal;
  margin-top: 0.5em;
  margin-left: 2em;
}
.image-title {
  text-align: center;
}
.responsive-image {
  object-fit: contain; /* アスペクト比を維持しながらコンテナに収める */
  max-width: 100%; /* 必要に応じて幅を調整 */
}
.image-date {
  text-align: center;
  font-size: 0.8em;
  color: #888;
}
</style>
